import { useState, useRef, useEffect } from "react";
import { cropImage } from "./lib/crop-utils.js";
import "./styles/animations.css";

export default function SelectionOverlay({ step, onCapture, imgRef, processing }) {
  const overlayRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);

  useEffect(() => {
    setStart(null);
    setEnd(null);
    setDrawing(false);
  }, [step.field]);

  // Les coordonnées sont prises par rapport à l'IMAGE, pas au calque : le calque
  // peut être plus large que l'image (voir cropImage), le rognage doit suivre l'image.
  const getPos = (e) => {
    const r = (imgRef.current || overlayRef.current).getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: cx - r.left, y: cy - r.top };
  };

  const handleDown = (e) => {
    if (processing) return;
    e.preventDefault();
    const p = getPos(e);
    setStart(p);
    setEnd(p);
    setDrawing(true);
  };

  const handleMove = (e) => {
    if (!drawing || processing) return;
    e.preventDefault();
    setEnd(getPos(e));
  };

  const handleUp = () => {
    if (!drawing || processing) return;
    setDrawing(false);
    if (start && end && Math.abs(end.x - start.x) > 15 && Math.abs(end.y - start.y) > 8) {
      const rect = {
        x: Math.min(start.x, end.x),
        y: Math.min(start.y, end.y),
        w: Math.abs(end.x - start.x),
        h: Math.abs(end.y - start.y),
      };
      const croppedBase64 = cropImage(imgRef.current, rect);
      onCapture(croppedBase64, rect);
    }
  };

  const rect = start && end
    ? { x: Math.min(start.x, end.x), y: Math.min(start.y, end.y), w: Math.abs(end.x - start.x), h: Math.abs(end.y - start.y) }
    : null;

  return (
    <div
      ref={overlayRef}
      onMouseDown={handleDown}
      onMouseMove={handleMove}
      onMouseUp={handleUp}
      onMouseLeave={() => { if (drawing) { setDrawing(false); setStart(null); setEnd(null); } }}
      onTouchStart={handleDown}
      onTouchMove={handleMove}
      onTouchEnd={handleUp}
      style={{ position: "absolute", inset: 0, cursor: processing ? "wait" : "crosshair", zIndex: 10, touchAction: "none" }}
    >
      {/* Instruction pill */}
      <div style={{
        position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
        background: step.color, color: "#fff", padding: "8px 20px", borderRadius: 99,
        fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
        display: "flex", alignItems: "center", gap: 8,
        boxShadow: `0 4px 20px ${step.color}55`, zIndex: 20, whiteSpace: "nowrap",
        animation: "slideDown 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}>
        <span style={{ fontSize: 16 }}>{step.icon}</span>
        {processing ? "\u26A1 Lecture OCR\u2026" : step.instruction}
      </div>

      {rect && rect.w > 5 && (
        <>
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
            <defs>
              <mask id={`cut-${step.field}`}>
                <rect width="100%" height="100%" fill="white" />
                <rect x={rect.x} y={rect.y} width={rect.w} height={rect.h} fill="black" rx="3" />
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.45)" mask={`url(#cut-${step.field})`} />
          </svg>
          <div style={{
            position: "absolute", left: rect.x, top: rect.y, width: rect.w, height: rect.h,
            border: `2.5px solid ${step.color}`, borderRadius: 3, pointerEvents: "none",
            boxShadow: processing ? `0 0 0 3px ${step.color}33, 0 0 20px ${step.color}22` : `0 0 0 1px ${step.color}33`,
            animation: processing ? "pulse-border 0.6s ease-in-out infinite" : "none",
          }}>
            {!processing && [
              { top: -5, left: -5 }, { top: -5, right: -5 }, { bottom: -5, left: -5 }, { bottom: -5, right: -5 },
            ].map((pos, i) => (
              <div key={i} style={{ position: "absolute", ...pos, width: 9, height: 9, background: step.color, borderRadius: 2 }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
